'use client'

import Link from 'next/link'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Button } from '@planner/ui'
import { formatPlanName } from '@/features/subscriptions/utils/subscriptionUtils'
import { isPlanEqualOrHigher } from '@/features/subscriptions/planConfig'
import type { SubscriptionPlan } from '@planner/types'

type UpsellReason = 'eventLimit' | 'featureLocked' | 'sectionLocked'

interface UpsellDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  reason: UpsellReason
  requiredPlan: SubscriptionPlan
  currentPlan: SubscriptionPlan
  isAdmin: boolean
}

function reasonCopy(reason: UpsellReason) {
  switch (reason) {
    case 'eventLimit':
      return 'Osiągnięto limit wydarzeń w Twoim planie.'
    case 'sectionLocked':
      return 'Ta sekcja nie jest dostępna w Twoim planie.'
    case 'featureLocked':
    default:
      return 'Ta funkcja nie jest dostępna w Twoim planie.'
  }
}

export function UpsellDialog({
  open,
  onOpenChange,
  reason,
  requiredPlan,
  currentPlan,
  isAdmin,
}: UpsellDialogProps) {
  // Don't show dialog if current plan is equal or higher than required plan
  const shouldShow = !isPlanEqualOrHigher(currentPlan, requiredPlan)
  
  // If current plan is higher, close the dialog
  if (open && !shouldShow) {
    onOpenChange(false)
    return null
  }
  
  return (
    <Dialog open={open && shouldShow} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Funkcja niedostępna</DialogTitle>
          <DialogDescription>
            {reasonCopy(reason)} Wymagany plan: <strong>{formatPlanName(requiredPlan)}</strong>. Obecny plan:{' '}
            <strong>{formatPlanName(currentPlan)}</strong>.
          </DialogDescription>
        </DialogHeader>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Zamknij
          </Button>
          {isAdmin ? (
            <Button 
              asChild
              className="relative z-10 [&>*]:relative [&>*]:z-10"
            >
              <Link href={`/subscription/upgrade?plan=${encodeURIComponent(requiredPlan)}&reason=${encodeURIComponent(reason)}`}>
                Zobacz upgrade
              </Link>
            </Button>
          ) : (
            <Button variant="secondary" disabled>
              Skontaktuj się z adminem swojej firmy
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

